import { Link } from 'react-router';
import { formatEuro, formatInteger } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Skeleton } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { useFinancingBoard } from '../api';
import type { BoardCard } from '../types';
import styles from './board.module.css';

export interface FinancingBoardProps {
  /** Search of the list header (`?q=`); the deals page passes none. */
  q?: string | undefined;
  className?: string | undefined;
}

function Card({ card }: { card: BoardCard }) {
  return (
    <Link
      to={`/financing/${card.id}`}
      className={styles.card}
      aria-label={`${card.contactName} · ${card.line}${card.overdueLabel ? ` · überfällig ${card.overdueLabel}` : ''}`}
    >
      <span className={styles.name}>{card.contactName}</span>
      <span className={styles.line}>{card.line}</span>
      <span className={styles.foot}>
        <span className={styles.amount}>
          {card.amountCents === null ? '—' : formatEuro(card.amountCents)}
        </span>
        {card.overdueLabel ? (
          <span className={clsx(styles.side, styles.overdue)}>{card.overdueLabel}</span>
        ) : card.financierShort ? (
          <span className={styles.side}>{card.financierShort}</span>
        ) : null}
      </span>
    </Link>
  );
}

/**
 * Finanzierungen 09 „Pipeline Finanzierung" (`GET /financings/board`): the stat line, six 220 px columns
 * with the A2 head (label · sum „362 T" · count) and the cards (→ record), the footnote. Stages follow
 * actions (§5.4) — there is no drag and drop. Shown in Tools › Finanzierungen („Board") and in
 * Tools › Deals with the switch „Finanzierung".
 */
export function FinancingBoard({ q = '', className }: FinancingBoardProps) {
  const board = useFinancingBoard(q);
  const data = board.data;
  const status = board.error instanceof ApiError ? board.error.status : null;

  return (
    <div className={clsx(styles.wrap, className)}>
      {data ? <div className={styles.stat}>{data.statLine}</div> : null}
      {board.isError ? (
        <Banner
          tone="danger"
          actions={
            <Button
              variant="destructiveFilled"
              size="small"
              loading={board.isFetching}
              onClick={() => void board.refetch()}
            >
              Erneut laden
            </Button>
          }
        >
          Karten konnten nicht geladen werden{status ? ` · Fehler ${status}` : ''}.{' '}
          {errorMessage(board.error)}
        </Banner>
      ) : null}
      {board.isPending ? (
        <div className={styles.board} aria-busy="true">
          {[1, 2, 3, 4, 5, 6].map((index) => (
            <div key={index} className={styles.column}>
              <Skeleton width="70%" height={12} />
              <Skeleton variant="block" height={88} />
            </div>
          ))}
        </div>
      ) : null}
      {data ? (
        <div className={clsx(styles.board, board.isPlaceholderData && styles.dimmed)}>
          {data.columns.map((column) => {
            const cards = data.cards.filter((card) => card.stageKey === column.key);
            return (
              <section key={column.key} className={styles.column} aria-label={column.label}>
                <div className={styles.columnHead}>
                  <span className={styles.columnLabel}>{column.label}</span>
                  <span className={styles.columnSum}>{column.sumLabel}</span>
                  <span className={styles.columnCount}>{formatInteger(column.count)}</span>
                </div>
                {cards.map((card) => (
                  <Card key={card.id} card={card} />
                ))}
              </section>
            );
          })}
        </div>
      ) : null}
      {data ? <p className={styles.footnote}>{data.footnote}</p> : null}
    </div>
  );
}
